import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { 
  Container,
  Form,
  Input,
  TextArea,
  Button,
  Message,
  Header,
  Segment
} from "semantic-ui-react";

export const FeedbackForm = () => {
  const {
    user,
    isAuthenticated,
    isLoading
  } = useAuth0();

  const [dbUser, setDbUser] = useState(null);

  const [message, setMessage] = useState("");
  const [photos, setPhotos] = useState([]);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  /*
   * Load the user's database profile
   */
  useEffect(() => {
    const loadOrCreateUser = async () => {
      if (!user || !isAuthenticated) return;

      const url = new URL(
        "http://localhost:1338/api/v1/users/by-email-or-create"
      );

      url.searchParams.append("email", user.email);
      url.searchParams.append(
        "first_name",
        user.given_name || ""
      );
      url.searchParams.append(
        "last_name",
        user.family_name || ""
      );
      url.searchParams.append(
        "gravatar",
        user.picture || ""
      );

      try {
        const res = await fetch(url.toString());

        if (!res.ok) {
          throw new Error("Unable to load user profile");
        }

        const data = await res.json();

        setDbUser(data);

      } catch (err) {
        console.error("Error loading user:", err);
        setError(true);
      }
    };

    loadOrCreateUser();
  }, [user, isAuthenticated]);

  /*
   * Handle photo selection
   */
  const handlePhotoChange = (e) => {
    setPhotos(Array.from(e.target.files));
  };

  /*
   * Submit feedback
   */
  const handleSubmit = async () => {
    if (!message.trim()) {
      setError(true);
      return;
    }

    setLoading(true);
    setError(false);
    setSuccess(false);

    try {
      /*
       * Convert photos to base64 so they can be
       * sent to Google Apps Script.
       */
      const photoData = await Promise.all(
        photos.map((file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = () => {
              resolve({
                name: file.name,
                type: file.type,
                data: reader.result.split(",")[1]
              });
            };

            reader.onerror = reject;

            reader.readAsDataURL(file);
          });
        })
      );

      /*
       * Submit directly to Google Apps Script
       */
      await fetch(
        "https://script.google.com/macros/s/AKfycbwjk0kklZCuuVronGsZM74-Wdnex451Ug4TzSwJz8h-OW8xJ1SEVszbF8lP6dcoFTaT8Q/exec",
        {
          method: "POST",
          mode: "no-cors",
          headers: {
            "Content-Type": "text/plain;charset=utf-8"
          },
          body: JSON.stringify({
            name : user.name,
            email: user.email,
            message: message,
            photos: photoData
          })
        }
      );

      /*
       * With mode: "no-cors", the response is opaque,
       * so response.ok cannot be checked.
       */

      setMessage("");
      setPhotos([]);
      setSuccess(true);

      const fileInput =
        document.getElementById("feedback-photos");

      if (fileInput) {
        fileInput.value = "";
      }

    } catch (err) {
      console.error("Feedback error:", err);
      setError(true);

    } finally {
      setLoading(false);
    }
  };

  /*
   * Loading states
   */
  if (isLoading || !isAuthenticated || !dbUser) {
    return (
      <Container className="view-container">
        <Segment>
          <p>Loading...</p>
        </Segment>
      </Container>
    );
  }

  return (
    <Container className="view-container">

      <Segment>

        <Header as="h2">
          Send Feedback
        </Header>

        <p>
          Have a suggestion, found an issue, or want to let us know
          something about the site? Send us a message below.
        </p>

        <Form
          onSubmit={handleSubmit}
          loading={loading}
          success={success}
          error={error}
        >

          <Message
            success
            header="Feedback received!"
            content="Thank you. Your feedback has been submitted."
          />

          <Message
            error
            header="Unable to submit feedback"
            content="Please enter a message and try again."
          />

          {/* User information */}

          <Form.Field>
            <label>Name</label>

            <Input
              value={`${user.name}`}
              disabled
            />
          </Form.Field>

          <Form.Field>
            <label>Email</label>

            <Input
              value={user.email}
              disabled
            />
          </Form.Field>

          {/* Feedback message */}

          <Form.Field required>
            <label>Message</label>

            <TextArea
              placeholder="Tell us what you think..."
              value={message}
              onChange={(e) =>
                setMessage(e.target.value)
              }
              style={{
                minHeight: 150
              }}
            />
          </Form.Field>

          {/* Photos */}

          <Form.Field>

            <label>Photos</label>

            <Input
              id="feedback-photos"
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoChange}
            />

            <small>
              Attach screenshots or photos that help explain
              your feedback.
            </small>

          </Form.Field>

          {/* Submit */}

          <Button
            primary
            type="submit"
            disabled={loading}
          >
            Send Feedback
          </Button>

        </Form>

      </Segment>

    </Container>
  );
};

